import { DeliveryType, OrderStatus, ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS, PaymentStatus, REFUND_STATUS_LABELS, RefundStatus } from '@jiaofa/constants'
import { formatMoney } from '@jiaofa/shared-utils'
import { FINANCE_TRANSACTION_STATUS, RECONCILIATION_STATUS, RECONCILIATION_UNASSIGNED_STORE_ID, type FinanceTransactionStatus, type ReconciliationStatus } from '@/api/finance'
import type { DeliveryArea, DeliveryRule } from '@/api/delivery'
import type { AdminCoupon, AdminCardTemplate } from '@/api/marketing'
import type { AdminInventory } from '@/api/product'

type Tone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

export function formatAmount(amountInFen: number): string { return `¥${formatMoney(amountInFen)}` }
export function getOrderStatusText(status: OrderStatus): string { return ORDER_STATUS_LABELS[status] ?? status }
export function getPaymentStatusText(status: PaymentStatus): string { return PAYMENT_STATUS_LABELS[status] ?? status }
// 业务只有门店自取与无人机配送：历史 DELIVERY 订单同样按无人机配送展示，不出现骑手配送
export function getDeliveryTypeText(type?: DeliveryType): string {
  if (type === DeliveryType.Pickup) return '门店自取'
  if (type === DeliveryType.Drone || type === DeliveryType.Delivery) return '无人机配送'
  return '--'
}
export function getRefundStatusText(status: RefundStatus): string { return REFUND_STATUS_LABELS[status] ?? status }
export function getRefundChannelText(channel?: string): string { return !channel ? '--' : channel === 'BALANCE' ? '余额退回' : channel === 'WECHAT' ? '微信原路退回' : channel }
export function getStoreStatusText(status: string): string { return status === 'OPEN' ? '营业中' : status === 'PAUSED' ? '暂停营业' : status === 'CLOSED' ? '已闭店' : status }
export function getProductStatusText(status: string): string { return status === 'ON' ? '在售' : status === 'OFF' ? '下架' : status }
export function getCategoryStatusText(status: string): string { return status === 'ENABLED' ? '启用' : status === 'DISABLED' ? '停用' : status }
export function getExtraStatusText(status: string): string { return status === 'ENABLED' ? '启用' : status === 'DISABLED' ? '停用' : status }
export function getComboStatusText(status?: string): string { return status === 'ENABLED' ? '启用' : status === 'DISABLED' ? '停用' : status || '--' }
export function getAdminStatusText(status?: string): string { return status === 'ENABLED' ? '启用' : status === 'DISABLED' ? '停用' : status || '--' }
export function getRoleStatusText(status?: string): string { return status === 'ENABLED' ? '启用' : status === 'DISABLED' ? '停用' : status || '--' }
export function getDataScopeText(scope?: string): string {
  return scope === 'ALL' ? '全部数据' : scope === 'STORE' ? '所属门店' : scope === 'SELF' ? '仅本人' : scope || '--'
}
export function getPaymentChannelText(channel?: string): string {
  return channel === 'WECHAT' ? '微信支付' : channel === 'ALIPAY' ? '支付宝' : channel === 'BALANCE' ? '余额支付' : channel || '--'
}
export function getVerificationResultText(result?: string): string {
  return result === 'SUCCESS' ? '成功' : result === 'FAIL' ? '失败' : result === 'DUPLICATE' ? '重复核销' : result || '--'
}
export function getPrinterStatusText(status?: string): string {
  return status === 'ONLINE' ? '在线' : status === 'OFFLINE' ? '离线' : status === 'PAPER_OUT' ? '缺纸' : status || '--'
}
export function getMessageSceneText(scene?: string): string {
  return scene === 'ORDER' ? '订单通知' : scene === 'PAYMENT' ? '支付通知' : scene === 'REFUND' ? '退款通知' : scene === 'MARKETING' ? '营销通知' : scene || '--'
}
export function getMessageChannelText(channel?: string): string {
  return channel === 'WECHAT' ? '微信' : channel === 'SMS' ? '短信' : channel === 'APP_PUSH' ? 'App 推送' : channel || '--'
}
export function getMessageLogStatusText(status?: string): string {
  return status === 'SUCCESS' ? '成功' : status === 'FAIL' ? '失败' : status === 'PENDING' ? '待发送' : status === 'PROCESSING' ? '发送中' : status || '--'
}
export function getMessageTemplateStatusText(status?: string): string {
  return status === 'ENABLED' ? '启用' : status === 'DISABLED' ? '停用' : status || '--'
}
export function getMessageConfigEnabledText(enabled?: boolean): string { return enabled ? '启用' : '停用' }
export function getDeliveryDistanceText(rule: DeliveryRule): string {
  const start = rule.startDistanceKm ?? 0
  const end = rule.maxDistanceKm ?? 0
  return `${start} - ${end} km`
}
export function getDeliveryAreaTypeText(type?: string): string {
  return type === 'RADIUS' ? '半径范围' : type === 'POLYGON' ? '多边形范围' : type || '--'
}
export function getDeliveryAreaValueText(area: DeliveryArea): string {
  if (area.type === 'RADIUS') return `${area.radiusKm ?? 0} km`
  return area.polygon || '--'
}
export function getUserLevelText(levelName?: string, level?: string): string {
  if (levelName) return levelName
  return level === 'BLACK' ? '黑金会员' : level === 'GOLD' ? '黄金会员' : level === 'SILVER' ? '白银会员' : level === 'NORMAL' ? '普通会员' : level || '--'
}
export function getUserStatusText(status?: string): string {
  return status === 'NORMAL' || status === 'ACTIVE' || status === 'ENABLED' ? '正常' : status === 'FROZEN' ? '冻结' : status === 'DISABLED' ? '停用' : status || '--'
}
export function getWalletLogTypeText(type?: string): string {
  return type === 'ADJUST' ? '后台调账' : type === 'CONSUME' ? '余额消费' : type === 'RECHARGE' ? '余额充值' : type || '--'
}
export function getPointsLogTypeText(type?: string): string {
  return type === 'ADJUST' ? '后台调整' : type === 'GAIN' ? '积分发放' : type === 'USE' ? '积分消耗' : type || '--'
}
export function formatSignedAmount(amount?: number): string {
  const value = amount ?? 0
  const prefix = value > 0 ? '+' : ''
  return `${prefix}${formatAmount(value)}`
}
export function formatSignedNumber(value?: number): string {
  const amount = value ?? 0
  return `${amount > 0 ? '+' : ''}${amount}`
}
export function getDataOrderStatusText(status?: string): string {
  return status ? ORDER_STATUS_LABELS[status as OrderStatus] ?? status : '--'
}
export function getFinanceTransactionStatusText(status: FinanceTransactionStatus): string {
  return status === FINANCE_TRANSACTION_STATUS.Success ? '成功' : status === FINANCE_TRANSACTION_STATUS.Processing ? '处理中' : status === FINANCE_TRANSACTION_STATUS.Failed ? '失败' : status
}
export function getReconciliationStatusText(status: ReconciliationStatus): string {
  return status === RECONCILIATION_STATUS.Balanced ? '已平账' : status === RECONCILIATION_STATUS.Pending ? '待确认' : status
}

/**
 * 对账行的门店名。
 *
 * 后端把 `store_id IS NULL` 的订单聚成 `storeId === '0'`、`storeName === ''` 的一行
 * （`FinanceService.toLong` 对非数字返回 0），直接 `storeName || storeId` 会显示成裸的 "0"。
 * 这里显式识别这个哨兵值。`storeName` 非空时优先用它。
 */
export function getReconciliationStoreNameText(item: { storeId: string; storeName?: string }): string {
  if (item.storeName) return item.storeName
  if (item.storeId === RECONCILIATION_UNASSIGNED_STORE_ID) return '未归属门店'
  return item.storeId || '--'
}
export function dataTrendTone(trend?: string): Tone {
  if (trend === '上升') return 'success'
  if (trend === '下降') return 'error'
  if (trend === '持平') return 'muted'
  return 'brand'
}
export function dataOrderStatusTone(status?: string): Tone {
  if (status === OrderStatus.Completed) return 'success'
  if ([OrderStatus.Cancelled, OrderStatus.Refunded].includes(status as OrderStatus)) return 'muted'
  if ([OrderStatus.Refunding].includes(status as OrderStatus)) return 'error'
  if ([OrderStatus.PendingAccept, OrderStatus.Producing, OrderStatus.PendingPickup, OrderStatus.Delivering].includes(status as OrderStatus)) return 'warning'
  return 'brand'
}
export function financeTransactionStatusTone(status: FinanceTransactionStatus): Tone {
  if (status === FINANCE_TRANSACTION_STATUS.Success) return 'success'
  if (status === FINANCE_TRANSACTION_STATUS.Processing) return 'warning'
  if (status === FINANCE_TRANSACTION_STATUS.Failed) return 'error'
  return 'brand'
}
export function reconciliationStatusTone(status: ReconciliationStatus): Tone {
  if (status === RECONCILIATION_STATUS.Balanced) return 'success'
  if (status === RECONCILIATION_STATUS.Pending) return 'warning'
  return 'brand'
}
export function getPaymentConfigStatusText(status?: string): string { return status === 'SET' ? '已配置' : status === 'UNSET' ? '未配置' : status || '--' }
export function getPaymentEnabledText(enabled?: boolean): string { return enabled ? '启用' : '停用' }
export function getCouponTypeText(type?: string): string { return type === 'DISCOUNT' ? '折扣券' : (type === 'CASH' || type === 'FULL_REDUCTION') ? '满减券' : type || '--' }
export function getCouponStatusText(status?: string): string { return status === 'ENABLED' ? '启用' : status === 'DISABLED' ? '停用' : status || '--' }
export function getCouponValueText(coupon: AdminCoupon): string {
  if (coupon.type === 'DISCOUNT') return `${coupon.discountRate ?? '--'} 折`
  return formatAmount(coupon.amount ?? 0)
}
export function getRechargeStatusText(status?: string): string { return status === 'ENABLED' ? '上架' : status === 'DISABLED' ? '下架' : status || '--' }
export function getCardCategoryText(category?: string): string { return category === 'BALANCE_CARD' ? '储值卡' : category === 'COUNT_CARD' ? '次卡' : category || '--' }
export function getCardStatusText(status?: string): string { return status === 'ENABLED' ? '启用' : status === 'DISABLED' ? '停用' : status || '--' }
export function getCardValueText(card: AdminCardTemplate): string {
  if (card.category === 'COUNT_CARD') return `${card.countValue ?? 0} 次`
  return formatAmount(card.faceValue ?? 0)
}
export function getGroupBuyStatusText(status?: string): string { return status === 'ENABLED' ? '启用' : status === 'DISABLED' ? '停用' : status === 'DRAFT' ? '草稿' : status || '--' }
export function getBargainStatusText(status?: string): string { return status === 'ENABLED' ? '启用' : status === 'DISABLED' ? '停用' : status === 'DRAFT' ? '草稿' : status || '--' }
export function getInviteStatusText(enabled?: boolean): string { return enabled ? '启用' : '停用' }
export function getFlashSaleStatusText(status?: string): string { return status === 'ENABLED' ? '启用' : status === 'DISABLED' ? '停用' : status === 'DRAFT' ? '草稿' : status || '--' }
export function getInventoryStatusText(inventory: AdminInventory): string {
  if (inventory.stock <= 0) return '售罄'
  if (inventory.safetyStock !== undefined && inventory.stock <= inventory.safetyStock) return '库存预警'
  return '库存正常'
}
export function canAuditRefund(status: RefundStatus): boolean { return [RefundStatus.Applying, RefundStatus.Reviewing].includes(status) }
export function orderStatusTone(status: OrderStatus): Tone {
  if (status === OrderStatus.Completed) return 'success'
  if ([OrderStatus.Cancelled, OrderStatus.Refunded].includes(status)) return 'muted'
  if (status === OrderStatus.Refunding) return 'error'
  if ([OrderStatus.PendingAccept, OrderStatus.Producing, OrderStatus.PendingPickup, OrderStatus.Delivering].includes(status)) return 'warning'
  return 'brand'
}
export function paymentStatusTone(status: PaymentStatus): Tone {
  if (status === PaymentStatus.Paid) return 'success'
  if (status === PaymentStatus.Refunded) return 'muted'
  if ([PaymentStatus.Failed, PaymentStatus.Closed].includes(status)) return 'error'
  if ([PaymentStatus.Paying, PaymentStatus.Refunding].includes(status)) return 'warning'
  return 'brand'
}
export function refundStatusTone(status: RefundStatus): Tone {
  if ([RefundStatus.Refunded, RefundStatus.Approved].includes(status)) return 'success'
  if ([RefundStatus.Applying, RefundStatus.Reviewing].includes(status)) return 'warning'
  if (status === RefundStatus.Rejected) return 'error'
  if ([RefundStatus.Closed, RefundStatus.None].includes(status)) return 'muted'
  return 'brand'
}
export function storeStatusTone(status: string): Tone {
  if (status === 'OPEN') return 'success'
  if (status === 'PAUSED') return 'warning'
  if (status === 'CLOSED') return 'muted'
  return 'brand'
}
export function productStatusTone(status: string): Tone {
  if (status === 'ON') return 'success'
  if (status === 'OFF') return 'muted'
  return 'brand'
}
export function comboStatusTone(status?: string): Tone {
  if (status === 'ENABLED') return 'success'
  if (status === 'DISABLED') return 'muted'
  return 'brand'
}
export function categoryStatusTone(status: string): Tone {
  if (status === 'ENABLED') return 'success'
  if (status === 'DISABLED') return 'muted'
  return 'brand'
}
export function extraStatusTone(status: string): Tone {
  if (status === 'ENABLED') return 'success'
  if (status === 'DISABLED') return 'muted'
  return 'brand'
}
export function adminStatusTone(status?: string): Tone {
  if (status === 'ENABLED') return 'success'
  if (status === 'DISABLED') return 'muted'
  return 'brand'
}
export function roleStatusTone(status?: string): Tone {
  if (status === 'ENABLED') return 'success'
  if (status === 'DISABLED') return 'muted'
  return 'brand'
}
export function verificationResultTone(result?: string): Tone {
  if (result === 'SUCCESS') return 'success'
  if (result === 'FAIL') return 'error'
  if (result === 'DUPLICATE') return 'warning'
  return 'muted'
}
export function printerStatusTone(status?: string): Tone {
  if (status === 'ONLINE') return 'success'
  if (status === 'PAPER_OUT') return 'warning'
  if (status === 'OFFLINE') return 'muted'
  return 'brand'
}
export function messageLogStatusTone(status?: string): Tone {
  if (status === 'SUCCESS') return 'success'
  if (status === 'FAIL') return 'error'
  if (status === 'PENDING' || status === 'PROCESSING') return 'warning'
  return 'muted'
}
export function messageTemplateStatusTone(status?: string): Tone {
  return status === 'ENABLED' ? 'success' : status === 'DISABLED' ? 'muted' : 'brand'
}
export function messageConfigTone(enabled?: boolean): Tone {
  return enabled ? 'success' : 'muted'
}
export function deliveryAreaTypeTone(type?: string): Tone {
  if (type === 'RADIUS') return 'success'
  if (type === 'POLYGON') return 'warning'
  return 'muted'
}
export function userLevelTone(level?: string): Tone {
  if (level === 'BLACK') return 'brand'
  if (level === 'GOLD') return 'warning'
  if (level === 'SILVER') return 'success'
  if (level === 'NORMAL') return 'muted'
  return 'muted'
}
export function userStatusTone(status?: string): Tone {
  if (status === 'NORMAL' || status === 'ACTIVE' || status === 'ENABLED') return 'success'
  if (status === 'FROZEN') return 'warning'
  if (status === 'DISABLED') return 'muted'
  return 'brand'
}
export function paymentConfigStatusTone(status?: string): Tone {
  if (status === 'SET') return 'success'
  if (status === 'UNSET') return 'warning'
  return 'brand'
}
export function paymentEnabledTone(enabled?: boolean): Tone {
  return enabled ? 'success' : 'muted'
}
export function couponStatusTone(status?: string): Tone {
  if (status === 'ENABLED') return 'success'
  if (status === 'DISABLED') return 'muted'
  return 'brand'
}
export function rechargeStatusTone(status?: string): Tone {
  if (status === 'ENABLED') return 'success'
  if (status === 'DISABLED') return 'muted'
  return 'brand'
}
export function cardStatusTone(status?: string): Tone {
  if (status === 'ENABLED') return 'success'
  if (status === 'DISABLED') return 'muted'
  return 'brand'
}
export function groupBuyStatusTone(status?: string): Tone {
  if (status === 'ENABLED') return 'success'
  if (status === 'DISABLED') return 'muted'
  if (status === 'DRAFT') return 'warning'
  return 'brand'
}
export function bargainStatusTone(status?: string): Tone {
  if (status === 'ENABLED') return 'success'
  if (status === 'DISABLED') return 'muted'
  if (status === 'DRAFT') return 'warning'
  return 'brand'
}
export function inviteStatusTone(enabled?: boolean): Tone {
  return enabled ? 'success' : 'muted'
}
export function flashSaleStatusTone(status?: string): Tone {
  if (status === 'ENABLED') return 'success'
  if (status === 'DISABLED') return 'muted'
  if (status === 'DRAFT') return 'warning'
  return 'brand'
}
export function inventoryStatusTone(inventory: AdminInventory): Tone {
  if (inventory.stock <= 0) return 'error'
  if (inventory.safetyStock !== undefined && inventory.stock <= inventory.safetyStock) return 'warning'
  return 'success'
}
